import { ICsvLeadRow } from '../../../services/apis/campaigns/useCampaignsRequest';

export type PostCommenter = {
  firstName?: string;
  lastName?: string;
  fullName?: string;
  name?: string;
  publicIdentifier?: string | null;
  headline?: string | null;
  profileUrl?: string | null;
  linkedinUrl?: string | null;
  profilePicture?: string;
  profileUrn?: string | null;
  commentText?: string;
  commentUrn?: string | null;
  /** Classic ACo / internal id when vanity is missing (Unipile). */
  linkedinId?: string;
};

export type GetPostCommentsPage = (params: {
  ugcPostId?: string;
  socialDetailUrn?: string;
  count?: number;
  start?: number;
  numReplies?: number;
  sortOrder?: string;
  paginationToken?: string;
}) => Promise<any>;

export type ResolvePostUrns = (params: {
  url: string;
  preferredId?: string;
}) => Promise<any>;

export type UnipilePostCommentersPageResult = {
  commenters?: PostCommenter[];
  cursor?: string | null;
  socialId?: string | null;
  totalItems?: number | null;
  commentCounter?: number | null;
  error?: string;
};

export type ImportPostCommentersUnipilePage = (params: {
  postUrl?: string;
  socialId?: string | null;
  cursor?: string | null;
  limit?: number;
}) => Promise<UnipilePostCommentersPageResult | null | false>;

const MAX_PAGES = 50;
const PAGE_SIZE = 10;
export const POST_UNIPILE_PAGE_SIZE = 50;
const POST_UNIPILE_MAX_PAGES = 40;

/** Human-like pause between comment pages (~1.8s–4.2s). */
function pageDelayMs() {
  return Math.floor(Math.random() * 2400) + 1800;
}

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function unwrapCommentsPage(raw: any): {
  ok: boolean;
  reason?: string;
  commenters: PostCommenter[];
  commentsLoaded: number;
  paginationToken: string | null;
  socialDetailUrn: string | null;
  threadUrn: string | null;
  totalComments: number | null;
} {
  if (raw instanceof Error) {
    return {
      ok: false,
      reason: raw.message || 'Failed to load comments.',
      commenters: [],
      commentsLoaded: 0,
      paginationToken: null,
      socialDetailUrn: null,
      threadUrn: null,
      totalComments: null,
    };
  }
  if (!raw || raw === false) {
    return {
      ok: false,
      reason: 'Failed to load comments.',
      commenters: [],
      commentsLoaded: 0,
      paginationToken: null,
      socialDetailUrn: null,
      threadUrn: null,
      totalComments: null,
    };
  }
  if (raw.status === false || raw.error) {
    return {
      ok: false,
      reason: raw.reason || raw.error || 'Failed to load comments.',
      commenters: [],
      commentsLoaded: 0,
      paginationToken: null,
      socialDetailUrn: null,
      threadUrn: null,
      totalComments: null,
    };
  }

  const parsed = raw.parsed || raw;
  const totalComments = Number(parsed?.totalComments);
  return {
    ok: true,
    commenters: Array.isArray(parsed?.commenters) ? parsed.commenters : [],
    commentsLoaded: Number(parsed?.commentsLoaded) || (Array.isArray(parsed?.commenters) ? parsed.commenters.length : 0),
    paginationToken: parsed?.paginationToken || null,
    socialDetailUrn: parsed?.socialDetailUrn || null,
    threadUrn: parsed?.threadUrn || null,
    totalComments: Number.isFinite(totalComments) && totalComments > 0 ? totalComments : null,
  };
}

function unwrapResolvedUrns(raw: any): { activityUrn: string | null; ugcPostUrn: string | null } {
  if (!raw || raw === false || raw instanceof Error) {
    return { activityUrn: null, ugcPostUrn: null };
  }
  const data = raw.result || raw;
  return {
    activityUrn: data?.activityUrn || null,
    ugcPostUrn: data?.ugcPostUrn || null,
  };
}

async function fetchCandidates(
  getPage: GetPostCommentsPage,
  candidates: string[],
  opts?: { onProgress?: (loaded: number, page: number) => void },
): Promise<{ commenters: PostCommenter[]; error?: string; tried: string[] }> {
  let lastError = '';
  const tried: string[] = [];

  for (const candidateUrn of candidates) {
    if (!candidateUrn || tried.includes(candidateUrn)) continue;
    tried.push(candidateUrn);

    const allCommenters: PostCommenter[] = [];
    let token: string | null = null;
    let start = 0;
    let socialDetailUrn: string | null = null;
    let pageCount = 0;
    let failed = false;

    while (pageCount < MAX_PAGES) {
      pageCount += 1;
      const raw = await getPage({
        ...(socialDetailUrn ? { socialDetailUrn } : { ugcPostId: candidateUrn }),
        count: PAGE_SIZE,
        start,
        numReplies: 1,
        // Match PostScanner — RELEVANCE paginates via token; reverse often does not.
        sortOrder: 'RELEVANCE',
        ...(token ? { paginationToken: token } : {}),
      });

      const page = unwrapCommentsPage(raw);
      if (!page.ok) {
        if (pageCount === 1) {
          lastError = page.reason || 'Failed to load comments.';
          failed = true;
        }
        break;
      }

      if (page.socialDetailUrn) socialDetailUrn = page.socialDetailUrn;

      allCommenters.push(...page.commenters);
      start += page.commentsLoaded || page.commenters.length;
      opts?.onProgress?.(allCommenters.length, pageCount);

      if (!page.commenters.length || !page.paginationToken) break;
      token = page.paginationToken;
      await sleep(pageDelayMs());
    }

    if (failed) continue;

    const seen = new Set<string>();
    const unique = allCommenters.filter((c) => {
      const key = c.publicIdentifier || c.linkedinId || c.profileUrn || c.commentUrn;
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    if (unique.length || candidateUrn === candidates[candidates.length - 1]) {
      return { commenters: unique, tried };
    }
  }

  return { commenters: [], error: lastError || undefined, tried };
}

/** Paginate LinkedIn post comments via the extension (same loop as PostScanner sidebar). */
export async function fetchAllPostCommenters(
  getPage: GetPostCommentsPage,
  ugcPostId: string,
  opts?: {
    postUrl?: string;
    preferredId?: string;
    resolvePostUrns?: ResolvePostUrns;
    onProgress?: (loaded: number, page: number) => void;
  },
): Promise<{ commenters: PostCommenter[]; error?: string }> {
  const id = String(ugcPostId || '').replace(/^urn:li:(?:activity|ugcPost):/i, '');
  const candidates = Array.from(new Set([
    ugcPostId?.startsWith?.('urn:li:') ? ugcPostId : null,
    id ? `urn:li:ugcPost:${id}` : null,
    id ? `urn:li:activity:${id}` : null,
  ].filter(Boolean) as string[]));

  const first = await fetchCandidates(getPage, candidates, opts);
  if (first.commenters.length) {
    return { commenters: first.commenters };
  }

  // Share slug IDs often aren't the comments thread — resolve real URNs from post HTML (like profile HTML).
  if (opts?.postUrl && opts?.resolvePostUrns) {
    try {
      const resolvedRaw = await opts.resolvePostUrns({
        url: opts.postUrl,
        preferredId: opts.preferredId || id || undefined,
      });
      const { activityUrn, ugcPostUrn } = unwrapResolvedUrns(resolvedRaw);
      const htmlCandidates = [activityUrn, ugcPostUrn].filter(
        (urn): urn is string => !!urn && !first.tried.includes(urn),
      );

      if (htmlCandidates.length) {
        const second = await fetchCandidates(getPage, htmlCandidates, opts);
        if (second.commenters.length || !second.error) {
          return { commenters: second.commenters, error: second.commenters.length ? undefined : second.error };
        }
        return { commenters: [], error: second.error || first.error };
      }
    } catch (e: any) {
      return { commenters: [], error: e?.message || first.error || 'Failed to resolve post URNs.' };
    }
  }

  return { commenters: [], error: first.error };
}

/**
 * Paginate post commenters via Unipile (proxy campaigns — no extension).
 * First page sends postUrl; later pages send socialId + cursor only.
 */
export async function fetchAllPostCommentersUnipile(opts: {
  postUrl: string;
  importPage: ImportPostCommentersUnipilePage;
  onProgress?: (loaded: number, page: number, total?: number | null) => void;
  pageSize?: number;
  pageDelayMs?: number;
}): Promise<{ commenters: PostCommenter[]; error?: string; totalItems?: number | null }> {
  const {
    postUrl,
    importPage,
    onProgress,
    pageSize = POST_UNIPILE_PAGE_SIZE,
    pageDelayMs: delayOverride,
  } = opts;

  const all: PostCommenter[] = [];
  const seen = new Set<string>();
  let cursor: string | null = null;
  let socialId: string | null = null;
  let totalItems: number | null = null;
  let page = 0;

  while (page < POST_UNIPILE_MAX_PAGES) {
    page += 1;
    const raw: UnipilePostCommentersPageResult | null | false = await importPage(
      page === 1
        ? { postUrl, limit: pageSize }
        : { socialId, cursor },
    );

    if (!raw) {
      return {
        commenters: all,
        totalItems,
        error: page === 1
          ? 'Could not load post comments. Check the LinkedIn account and post URL.'
          : `Stopped after ${all.length} commenters — page ${page} failed.`,
      };
    }

    if (raw.error && !(Array.isArray(raw.commenters) && raw.commenters.length)) {
      return { commenters: all, totalItems, error: raw.error };
    }

    if (raw.socialId) socialId = String(raw.socialId);
    const totalRaw = raw.totalItems ?? raw.commentCounter;
    if (typeof totalRaw === 'number' && Number.isFinite(totalRaw)) {
      totalItems = totalRaw;
    }

    const batch = Array.isArray(raw.commenters) ? raw.commenters : [];
    for (const c of batch) {
      const key = (c.publicIdentifier || c.linkedinId || c.profileUrn || c.commentUrn || '').trim();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      all.push(c);
    }

    onProgress?.(all.length, page, totalItems);

    const next: string | null = raw.cursor ? String(raw.cursor) : null;
    if (!next || !batch.length) break;
    cursor = next;
    await sleep(typeof delayOverride === 'number' ? delayOverride : pageDelayMs());
  }

  return { commenters: all, totalItems };
}

function linkedinIdFromProfileUrn(profileUrn?: string | null): string | undefined {
  if (!profileUrn) return undefined;
  const m = String(profileUrn).match(/fsd_profile:([^/?#]+)/i);
  return m?.[1] || undefined;
}

/** Map extension/Unipile commenters → CSV-style enroll rows (people only). */
export function commentersToLeadRows(commenters: PostCommenter[]): ICsvLeadRow[] {
  const rows: ICsvLeadRow[] = [];
  const seen = new Set<string>();

  for (const c of commenters) {
    const publicIdentifier = (c.publicIdentifier || '').trim() || undefined;
    const linkedinId = (c.linkedinId || '').trim()
      || linkedinIdFromProfileUrn(c.profileUrn)
      || undefined;
    const key = publicIdentifier || linkedinId;
    if (!key || seen.has(key)) continue;
    if (publicIdentifier) seen.add(publicIdentifier);
    if (linkedinId) seen.add(linkedinId);

    rows.push({
      publicIdentifier,
      linkedinId,
      firstName: c.firstName || undefined,
      lastName: c.lastName || undefined,
      name: c.fullName || c.name || undefined,
      linkedinUrl: c.profileUrl || c.linkedinUrl || undefined,
      title: c.headline || undefined,
      imageUrl: c.profilePicture || undefined,
    });
  }

  return rows;
}
